
import { validateCardWithStripe } from '@/utils/stripe/cardVerificationService';
import { secureStoreCardDetails } from '@/utils/secureLocalStorage';
import { toast } from '@/components/ui/use-toast';
import { CardDetails, CardVerificationResult } from './types';

export async function performStripeVerification(
  card: CardDetails, 
  aiResult: CardVerificationResult
): Promise<CardVerificationResult> {
  console.log('AI verification passed but requiring Stripe verification for higher confidence', card);
  
  try {
    // Ensure card details are properly formatted before passing to Stripe
    const normalizedCardDetails = {
      cardNumber: card.cardNumber.replace(/\s+/g, ''), // Remove any spaces
      expiryDate: card.expiryDate,
      cvv: card.cvv,
      cardholderName: card.cardholderName || 'Card Holder' // Provide default if missing
    };
    
    console.log('Sending normalized card details to Stripe', {
      cardNumberLength: normalizedCardDetails.cardNumber.length,
      expiryDate: normalizedCardDetails.expiryDate,
      cvvProvided: !!normalizedCardDetails.cvv,
      cardholderName: normalizedCardDetails.cardholderName
    });
    
    const stripeResult = await validateCardWithStripe(normalizedCardDetails);
    
    if (!stripeResult.isValid) {
      toast({
        title: "Stripe Verification Failed",
        description: "Your card passed AI verification but failed Stripe verification. Please check your card details and try again.",
        variant: "destructive",
      });
      
      return {
        success: false,
        message: 'Card failed Stripe verification. Please try again with a different card.',
        score: aiResult.score,
        riskLevel: aiResult.riskLevel,
        stripeReady: false
      };
    }
    
    // Calculate combined score
    const combinedScore = Math.round((aiResult.score + stripeResult.score) / 2);
    
    try {
      await secureStoreCardDetails({
        lastFourDigits: stripeResult.last4 || card.cardNumber.slice(-4),
        cardholderName: card.cardholderName,
        expiryDate: card.expiryDate,
        timestamp: new Date().toISOString(),
        verified: true,
        verificationScore: combinedScore
      });
    } catch (storageError) {
      console.error('Error storing card details:', storageError);
      // Continue even if storage fails
    }
    
    toast({
      title: "Card Verified Successfully",
      description: `Your card has been verified by our AI system and Stripe with a score of ${combinedScore}/100.`,
    });
    
    return {
      success: true,
      message: 'Card verified by AI security system and Stripe',
      score: combinedScore,
      riskLevel: aiResult.riskLevel,
      stripeReady: true,
      cardBrand: stripeResult.brand,
      last4: stripeResult.last4,
      stripePaymentMethodId: stripeResult.token, // Add the stripePaymentMethodId
      stripeResponse: {
        id: stripeResult.token,
        card: {
          brand: stripeResult.brand,
          last4: stripeResult.last4
        }
      }
    };
  } catch (error) {
    console.error('Stripe verification error:', error);
    
    toast({
      title: "Verification Error",
      description: "An unexpected error occurred during Stripe verification. Please try again.",
      variant: "destructive",
    });
    
    return {
      success: false,
      message: 'Stripe verification system error',
      score: aiResult.score,
      riskLevel: aiResult.riskLevel,
      stripeReady: false
    };
  }
}
